<template>
  <modal :name="name" :width="480" height="auto" :adaptive="true" :scrollable="true" :focus-trap="true" :clickToClose="false" @before-open="loadTemplates" @before-close="beforeClose" @opened="focusHeading" @closed="$emit('closed')">
    <div class="popup booking-email-modal" role="dialog" aria-modal="true" :aria-labelledby="`${name}-heading`" :aria-busy="loading || sending" @keydown.esc.prevent.stop="close">
      <button class="popup__close" type="button" :disabled="sending" aria-label="Close Resend E-mail" @click="close" />
      <h2 :id="`${name}-heading`" ref="heading" class="popup__title" tabindex="-1">Resend E-mail</h2>
      <div class="popup__body">
        <app-block-loader v-if="loading" min-height="100px" :rows="2">Loading templates…</app-block-loader>
        <div v-else-if="catalogError" class="booking-email-modal__error" role="alert">
          <p>{{ catalogError }}</p>
          <app-button theme="dark" size="small" @click.native="loadTemplates">Retry</app-button>
        </div>
        <p v-else-if="!templates.length" class="booking-email-modal__message" role="status">No suitable email templates are available for this booking.</p>
        <form v-else @submit.prevent="submit">
          <app-form-group label="Template" :label-for="`${name}-template`" required>
            <app-select v-model="templateId" :input-id="`${name}-template`" aria-label="Email template" :options="templates" :reduce="template => template.sendpulse_template_id" :clearable="false" :disabled="sending || uncertain" label="name" placeholder="Choose template" />
          </app-form-group>
          <p v-if="sendError" class="booking-email-modal__error" role="alert">{{ sendError }}</p>
          <p v-if="uncertain" class="booking-email-modal__message" role="status">The result could not be verified. Close this window and check the email history before trying again.</p>
          <footer class="booking-email-modal__footer">
            <app-button theme="dark" :disabled="sending" @click.native="close">Cancel</app-button>
            <app-button type="submit" :disabled="!canSubmit">{{ sending ? 'Sending…' : 'Send' }}</app-button>
          </footer>
        </form>
      </div>
    </div>
  </modal>
</template>

<script>
import { getFlightsBookingEmailTemplates, postFlightsBookingEmails } from '@/http'
import { responseError } from './booking-detail'

export default {
  name: 'BookingEmailResendModal',
  props: { name: { type: String, required: true }, booking: { type: Object, required: true } },
  data() {
    return { templates: [], templateId: null, loading: false, sending: false, catalogError: '', sendError: '', uncertain: false, sessionId: 0 }
  },
  computed: {
    canSubmit() {
      return !this.loading && !this.sending && !this.catalogError && !this.uncertain && this.templates.some(template => template.sendpulse_template_id === this.templateId)
    }
  },
  beforeDestroy() { this.sessionId++ },
  methods: {
    async loadTemplates() {
      if (this.sending) return
      const sessionId = ++this.sessionId
      this.templates = []
      this.templateId = null
      this.catalogError = ''
      this.sendError = ''
      this.uncertain = false
      this.loading = true
      try {
        const { data } = await getFlightsBookingEmailTemplates(this.booking.id)
        if (sessionId !== this.sessionId) return
        if (!Array.isArray(data)) throw new Error('Invalid template list response')
        this.templates = data
        if (data.length === 1) this.templateId = data[0].sendpulse_template_id
      } catch (error) {
        if (sessionId === this.sessionId) this.catalogError = responseError(error, 'Unable to load email templates.')
      } finally {
        if (sessionId === this.sessionId) this.loading = false
      }
    },
    async submit() {
      if (!this.canSubmit) return
      const sessionId = this.sessionId
      const bookingId = this.booking.id
      this.sending = true
      this.sendError = ''
      try {
        await postFlightsBookingEmails(bookingId, { sendpulse_template: this.templateId })
        if (sessionId !== this.sessionId) return
        this.sending = false
        this.$notify({ type: 'success', title: 'Email request created.' })
        this.$emit('refresh')
        this.close()
      } catch (error) {
        if (sessionId === this.sessionId) {
          this.sendError = responseError(error, 'Unable to send email.')
          this.uncertain = !error.response || error.response.status >= 500
          if (this.uncertain) this.$emit('refresh')
        }
      } finally {
        if (sessionId === this.sessionId) this.sending = false
      }
    },
    focusHeading() { if (this.$refs.heading) this.$refs.heading.focus() },
    close() { if (!this.sending) this.$modal.hide(this.name) },
    beforeClose(event) {
      if (this.sending) event.cancel()
      else this.sessionId++
    }
  }
}
</script>

<style lang="scss" scoped>
.booking-email-modal { color: #20241d; font: 400 15px/1.5 $font-family-base; }
.booking-email-modal .popup__title { margin-bottom: 20px; padding-right: 30px; font-size: 20px; font-weight: 500; text-align: left; cursor: default; }
.booking-email-modal__message { margin: 0; color: $color-text-second; }
.booking-email-modal__error { margin: 0 0 16px; color: $color-error; overflow-wrap: anywhere; }
.booking-email-modal__error p { margin: 0 0 12px; }
.booking-email-modal__footer { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 12px; margin-top: 20px; padding-top: 16px; border-top: 1px solid $color-border; }
.booking-email-modal__footer .btn { min-width: auto; }
.booking-email-modal .popup__close:focus-visible { outline: 2px solid $color-theme; outline-offset: 3px; }
</style>
